// src/pages/Terms.jsx
import React from "react";

export default function Terms() {
  React.useEffect(() => {
    document.title = "Terms of Use";
  }, []);

  const [isSmallScreen, setIsSmallScreen] = React.useState(false);
  const [openIndex, setOpenIndex] = React.useState(false);

  React.useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = () => setIsSmallScreen(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  const pageStyle = {
    ...styles.page,
    ...(isSmallScreen ? styles.pageMobile : null),
  };

  const titleStyle = {
    ...styles.title,
    ...(isSmallScreen ? styles.titleMobile : null),
  };

  const trustGridStyle = {
    ...styles.trustGrid,
    ...(isSmallScreen ? styles.trustGridMobile : null),
  };

  const layoutStyle = {
    ...styles.layout,
    ...(isSmallScreen ? styles.layoutMobile : null),
  };

  const footerGridStyle = {
    ...styles.footerGrid,
    ...(isSmallScreen ? styles.footerGridMobile : null),
  };

  const indexItems = [
    ["legal-advice", "No Legal Advice"],
    ["verification", "Independent Verification"],
    ["ai-limitations", "AI & Technology Limitations"],
    ["services", "Service-Specific Terms"],
    ["acceptable-use", "Acceptable Use"],
    ["payments", "Payments & Refunds"],
    ["user-content", "User Content"],
    ["ip", "Intellectual Property"],
    ["privacy", "Privacy"],
    ["indemnification", "Indemnification"],
    ["disclaimers", "Disclaimers"],
    ["liability", "Limitation of Liability"],
    ["termination", "Suspension & Termination"],
    ["law", "Governing Law"],
    ["contact", "Contact"],
  ];

  return (
    <div style={pageStyle}>
      <section style={styles.header}>
        <h1 style={titleStyle}>Terms of Use</h1>

        <div style={styles.metaRow}>
          <span><strong>Effective Date:</strong> September 29, 2025</span>
          <span style={styles.metaDivider}>|</span>
          <span><strong>Last Updated:</strong> September 29, 2025</span>
        </div>

        <p style={styles.intro}>
          By creating an account, accessing, purchasing, or using the Services, you agree to these
          Terms of Use (the “Terms”). These Terms govern your access to and use of the services
          operated by <strong>NexGen Automate, Inc.</strong> (“Company,” “we,” “us,” or “our”),
          including AskLawly.com, AskLawly.io, AskLawly.ai, related mobile apps, and any tools,
          features, or content we provide (collectively, the “Services”).
        </p>
      </section>

      <section style={trustGridStyle} aria-label="Important terms summary">
        <TrustItem
          icon="⚖"
          title="Not a law firm"
          text="We do not provide legal advice or legal representation."
        />
        <TrustItem
          icon="!"
          title="AI can be wrong"
          text="Outputs may be incomplete, outdated, or inaccurate."
        />
        <TrustItem
          icon="✓"
          title="You must verify"
          text="Review outputs and consult licensed professionals when appropriate."
        />
      </section>

      {isSmallScreen && (
        <section style={styles.mobileIndex}>
          <button
            type="button"
            style={styles.mobileIndexButton}
            onClick={() => setOpenIndex((prev) => !prev)}
            aria-expanded={openIndex}
          >
            <span>In These Terms</span>
            <span>{openIndex ? "−" : "⌄"}</span>
          </button>
          {openIndex && (
            <div style={styles.mobileIndexList}>
              {indexItems.map(([id, label]) => (
                <a key={id} href={`#${id}`} style={styles.mobileIndexLink}>
                  {label}
                </a>
              ))}
            </div>
          )}
        </section>
      )}

      <div style={layoutStyle}>
        {!isSmallScreen && (
          <aside style={styles.sidebar}>
            <div style={styles.sidebarTitle}>In These Terms</div>
            <nav style={styles.navList}>
              {indexItems.map(([id, label], index) => (
                <a
                  key={id}
                  href={`#${id}`}
                  style={{
                    ...styles.navLink,
                    ...(index === 0 ? styles.navLinkActive : null),
                  }}
                >
                  <span style={styles.navIcon} aria-hidden="true">•</span>
                  <span>{label}</span>
                </a>
              ))}
            </nav>

            <div style={styles.sidebarNote}>
              <div style={styles.sidebarNoteIcon}>▣</div>
              <div>
                <strong>Important.</strong>
                <p style={styles.sidebarNoteText}>
                  These Terms are legally binding and should be read together with our Privacy Policy.
                </p>
              </div>
            </div>
          </aside>
        )}

        <main style={styles.policyPanel}>
          <PolicySection id="legal-advice" number="1" title="No Legal Advice — Educational Use Only">
            <p style={styles.p}>
              Our Services provide general legal information and software-generated outputs. Content
              is for <strong>educational and informational purposes only</strong> and is{" "}
              <strong>not legal advice</strong>. We are <strong>not a law firm</strong>, we do{" "}
              <strong>not</strong> provide legal representation, and{" "}
              <strong>no attorney-client relationship</strong> is formed by using the Services.
              Always consult a licensed attorney for advice about your specific situation.
            </p>
          </PolicySection>

          <Callout id="professional-warning" icon="!" title="Professional-use caution" tone="blue">
            <p style={styles.calloutText}>
              Do not use outputs as the sole basis for legal, financial, tax, immigration, medical,
              or other important decisions. You are responsible for reviewing outputs and seeking
              help from qualified professionals when appropriate.
            </p>
          </Callout>

          <PolicySection id="verification" number="2" title="Independent Verification; Your Responsibility">
            <p style={styles.p}>
              You agree to <strong>conduct your own due diligence</strong> and confirm the accuracy,
              timeliness, completeness, and applicability of any output before relying on it. Laws,
              procedures, facts, filing requirements, court practices, agency policies, and
              professional standards change frequently and vary by jurisdiction. You remain solely
              responsible for your decisions, filings, communications, and actions.
            </p>
          </PolicySection>

          <PolicySection id="ai-limitations" number="3" title="AI & Technology Limitations">
            <p style={styles.p}>
              AI systems may generate incomplete, outdated, biased, misleading, or incorrect content
              (“hallucinations”). We provide the Services on an <strong>“as is”</strong> and{" "}
              <strong>“as available”</strong> basis without warranties of any kind. We do not
              guarantee that outputs will be correct, complete, current, enforceable, lawful, or
              suitable for any purpose.
            </p>
          </PolicySection>

          <PolicySection id="services" number="4" title="Service-Specific Terms">
            <ServiceBlock title="a) Case Estimator">
              <li style={styles.li}>Provides directional estimates of potential attorney fees and out-of-pocket costs based on user inputs and public or derived data.</li>
              <li style={styles.li}>Estimates are <strong>not quotes</strong>, <strong>not guarantees</strong>, and may be inaccurate. Actual pricing is set solely by the attorney or professional you hire.</li>
              <li style={styles.li}>Summaries may be shown in multiple languages for convenience; English controls in the event of discrepancies.</li>
            </ServiceBlock>

            <ServiceBlock title="b) Case Value">
              <li style={styles.li}>Provides a <strong>directional, informational view</strong> of potential case value based on scenario inputs. It is <strong>not an appraisal</strong>, valuation, legal opinion, or prediction.</li>
              <li style={styles.li}>Real outcomes depend on facts, law, evidence, counsel, jurisdiction, decision-makers, and many unknown factors. Consult a licensed attorney.</li>
            </ServiceBlock>

            <ServiceBlock title="c) Chat">
              <li style={styles.li}>Provides general, educational information; <strong>not legal advice</strong> or a substitute for an attorney.</li>
              <li style={styles.li}>Outputs may be inaccurate or incomplete; verify independently before use.</li>
              <li style={styles.li}>Session-based access may include limits, including time, messages, usage, plan features, and fees.</li>
            </ServiceBlock>

            <ServiceBlock title="d) Contract Insight">
              <li style={styles.li}>Provides an educational contract review, such as key terms, risks, red flags, and common missing protections. It is <strong>not legal advice</strong>.</li>
              <li style={styles.li}>Contract analysis may support uploads up to <strong>30 pages per file</strong>; longer, image-heavy, scanned, or low-quality documents may take more time or produce less reliable results.</li>
              <li style={styles.li}>Outputs can be incomplete or incorrect. You must <strong>verify</strong> and consult a licensed attorney before making decisions with legal consequences.</li>
            </ServiceBlock>

            <ServiceBlock title="e) DocWriter">
              <li style={styles.li}>Generates draft documents using AI, schemas, options, facts, and instructions you select or provide. Drafts are provided <strong>as templates only</strong>, not as legal documents or professional advice.</li>
              <li style={styles.li}>You must <strong>review, edit, and verify</strong> all drafts and consult a licensed professional where appropriate.</li>
              <li style={styles.li}>We <strong>do not guarantee</strong> correctness, completeness, enforceability, legality, or suitability of any draft.</li>
            </ServiceBlock>

            <ServiceBlock title="f) FileVault">
              <li style={styles.li}>Provides personal file storage with security controls; capacity may be limited, including free-plan storage limits.</li>
              <li style={styles.li}>The Service is <strong>not an archival or records-retention system</strong>. Availability may be interrupted for maintenance, outages, or events beyond our control. You agree to <strong>maintain your own independent backups</strong>.</li>
              <li style={styles.li}>Do not upload audio/video files, executable files, archives, malware, illegal content, infringing content, or content you do not have rights or consent to upload. Avoid storing unnecessary sensitive personal data.</li>
            </ServiceBlock>

            <ServiceBlock title="g) Smart Alert">
              <li style={styles.li}>Sends notifications, including SMS and email, to contacts you designate. Delivery is <strong>not guaranteed</strong> and depends on third-party carriers, networks, providers, device settings, and recipient availability.</li>
              <li style={styles.li}>International delivery may be limited, delayed, blocked, or subject to local infrastructure, carrier rules, or regulations.</li>
              <li style={styles.li}>You are responsible for message content, recipient consent, and compliance with applicable laws when using Smart Alert.</li>
            </ServiceBlock>

            <ServiceBlock title="h) Vision Assist">
              <li style={styles.li}>Provides step-by-step guidance based on your screen context, screen sharing where supported, screenshots, and uploaded documents. It is <strong>not legal advice</strong>.</li>
              <li style={styles.li}>Document uploads may support up to <strong>30 pages per upload</strong>. Guidance may be delivered page-by-page with summaries.</li>
              <li style={styles.li}>Screen sharing and real-time analysis depend on device, browser, operating system, permissions, and network capabilities and may not work reliably in all environments.</li>
              <li style={styles.li}>Do not share passwords, full payment card numbers, or unnecessary sensitive information during screen sharing.</li>
            </ServiceBlock>
          </PolicySection>

          <PolicySection id="acceptable-use" number="5" title="Acceptable Use & Prohibited Content">
            <ul style={styles.list}>
              <li style={styles.li}>No illegal, infringing, fraudulent, deceptive, harmful, or abusive activity.</li>
              <li style={styles.li}>No pornography, sexual content involving minors, harassment, threats, hate content, exploitation, or content that violates the rights or safety of others.</li>
              <li style={styles.li}>No malware, spyware, scraping, credential harvesting, reverse engineering, unauthorized access, attempts to bypass security, or attempts to disrupt the Services.</li>
              <li style={styles.li}>No uploading audio/video, executables, compressed archives, malware, illegal content, or infringing content to FileVault.</li>
              <li style={styles.li}>Respect privacy, confidentiality, intellectual-property, and data-protection laws. Do not upload or submit data you lack rights, authority, or consent to share.</li>
              <li style={styles.li}>Do not use the Services to create or facilitate legal, financial, tax, immigration, or other professional work for others in a way that violates licensing or professional-responsibility rules.</li>
            </ul>
          </PolicySection>

          <PolicySection id="availability" number="6" title="Availability, Maintenance & Changes">
            <p style={styles.p}>
              We may modify, suspend, limit, replace, or discontinue any part of the Services at any
              time, with or without notice. We are not responsible for delays, failures, outages,
              degraded performance, data loss, or unavailable features due to maintenance, third-party
              providers, network issues, software defects, user devices, security incidents, legal
              requirements, or events beyond our reasonable control.
            </p>
          </PolicySection>

          <PolicySection id="monitoring" number="7" title="Monitoring & Logging">
            <p style={styles.p}>
              For security, abuse prevention, quality, support, billing, compliance, and service
              improvement, we may monitor and log interactions, including chats, uploads, generated
              outputs, alerts, screen-share/session metadata, and usage activity, consistent with our{" "}
              <a href="/privacy" style={styles.link}>Privacy Policy</a>.
            </p>
          </PolicySection>

          <PolicySection id="payments" number="8" title="Payments, Renewals, Credits & Refunds">
            <ul style={styles.list}>
              <li style={styles.li}>Paid features, subscriptions, sessions, credits, add-ons, and Smart Alert usage are billed as disclosed at purchase.</li>
              <li style={styles.li}><strong>All sales are final and non-refundable</strong> unless required by law or expressly stated in writing by us.</li>
              <li style={styles.li}>Subscriptions renew automatically until canceled. Cancel before renewal to avoid future charges. Cancellation stops future renewals but does not automatically refund prior charges.</li>
              <li style={styles.li}>Plan features, limits, pricing, storage, credits, allowances, and included tools may change prospectively. We may provide notice where required.</li>
              <li style={styles.li}>Credits, usage allowances, trial benefits, and promotional access may be non-transferable, have no cash value, and expire as stated at purchase or in the product interface.</li>
              <li style={styles.li}>Taxes, fees, currency-conversion costs, carrier charges, or third-party charges may apply depending on your location, payment method, and use of the Services.</li>
            </ul>
          </PolicySection>

          <PolicySection id="user-content" number="9" title="User Content & License to Operate the Services">
            <p style={styles.p}>
              You retain ownership of content you upload, submit, transmit, or generate through the
              Services, subject to any rights held by others. You grant us a limited, worldwide,
              non-exclusive, royalty-free license to host, store, reproduce, process, transmit,
              display, analyze, and use your content as reasonably necessary to provide, secure,
              operate, support, troubleshoot, enforce, and improve the Services, consistent with our{" "}
              <a href="/privacy" style={styles.link}>Privacy Policy</a>.
            </p>
            <p style={styles.p}>
              You represent that you have all rights, permissions, and consents necessary to submit
              your content and to authorize us and our service providers to process it for the
              Services. You are responsible for your content and for ensuring it does not violate law,
              confidentiality duties, intellectual-property rights, privacy rights, or these Terms.
            </p>
          </PolicySection>

          <PolicySection id="ip" number="10" title="Intellectual Property">
            <p style={styles.p}>
              The Services, software, designs, interfaces, workflows, prompts, models, reports,
              templates, text, graphics, logos, documentation, and content we provide are owned by us
              or our licensors and are protected by law. You receive a limited, revocable,
              non-transferable, non-sublicensable license to use the Services for personal or
              internal business purposes, subject to these Terms.
            </p>
          </PolicySection>

          <PolicySection id="privacy" number="11" title="Privacy">
            <p style={styles.p}>
              Your use of the Services is also governed by our{" "}
              <a href="/privacy" style={styles.link}>Privacy Policy</a>. By using the Services, you
              consent to our data practices described there. The Privacy Policy explains our data
              collection, use, disclosure, retention, AI processing, service providers, user rights,
              and security practices.
            </p>
          </PolicySection>

          <PolicySection id="indemnification" number="12" title="Indemnification">
            <p style={styles.p}>
              You agree to indemnify, defend, and hold harmless the Company and its officers,
              directors, employees, contractors, affiliates, service providers, and agents from and
              against any claims, losses, liabilities, damages, costs, and expenses, including
              reasonable attorneys’ fees, arising from or related to your use of the Services, your
              content, your violation of these Terms, your violation of law, or your violation of any
              third-party rights.
            </p>
          </PolicySection>

          <PolicySection id="disclaimers" number="13" title="Disclaimers">
            <p style={styles.allCaps}>
              TO THE FULLEST EXTENT PERMITTED BY LAW, THE SERVICES ARE PROVIDED “AS IS” AND “AS
              AVAILABLE,” WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY,
              INCLUDING WITHOUT LIMITATION WARRANTIES OF ACCURACY, COMPLETENESS, TIMELINESS,
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, QUIET ENJOYMENT,
              SECURITY, AVAILABILITY, OR RELIABILITY.
            </p>
          </PolicySection>

          <PolicySection id="liability" number="14" title="Limitation of Liability">
            <p style={styles.allCaps}>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE COMPANY SHALL NOT BE LIABLE FOR ANY
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY
              LOSS OF PROFITS, REVENUE, DATA, FILES, BUSINESS OPPORTUNITY, GOODWILL, OR REPUTATION.
              OUR TOTAL LIABILITY FOR ANY CLAIM RELATING TO THE SERVICES SHALL NOT EXCEED THE
              AMOUNTS YOU PAID TO US FOR THE SERVICES IN THE SIX (6) MONTHS PRECEDING THE EVENT
              GIVING RISE TO THE CLAIM.
            </p>
          </PolicySection>

          <PolicySection id="termination" number="15" title="Suspension & Termination">
            <p style={styles.p}>
              We may suspend, restrict, or terminate access for violations of these Terms, suspected
              fraud or abuse, legal requirements, security concerns, nonpayment, risk to the Services,
              or risk to users or others. You may stop using the Services at any time. Termination
              does not relieve you of payment obligations, accrued liabilities, or provisions that by
              their nature should survive.
            </p>
          </PolicySection>

          <PolicySection id="law" number="16" title="Governing Law & Dispute Resolution">
            <p style={styles.p}>
              These Terms are governed by the laws of the <strong>State of Delaware, USA</strong>,
              without regard to conflict-of-laws rules. You agree to the exclusive jurisdiction and
              venue of state and federal courts located in Delaware, unless another venue or dispute
              process is required by applicable law.
            </p>
          </PolicySection>

          <PolicySection id="updates" number="17" title="Updates to These Terms">
            <p style={styles.p}>
              We may update these Terms from time to time. Material changes will be noted by
              updating the “Last Updated” date above or via in-product notice where appropriate. Your
              continued use of the Services after changes means you accept the updated Terms.
            </p>
          </PolicySection>

          <Callout id="summary" icon="i" title="Summary (non-binding)" tone="green">
            <p style={styles.calloutText}>
              We are not a law firm; our AI and tools are educational only. Outputs can be wrong.
              Always verify with a licensed professional. Storage, alerts, uploads, screen sharing,
              and availability are not guaranteed. Keep your own backups. Paid purchases may be
              final and non-refundable unless required by law.
            </p>
          </Callout>
        </main>
      </div>

      <section style={styles.footerPanel}>
        <h2 style={styles.footerTitle}>Ownership & Contact</h2>
        <div style={footerGridStyle}>
          <FooterItem icon="▥" text={<><strong>NexGen Automate, Inc.</strong></>} />
          <FooterItem icon="◎" text={<><strong>AskLawly.com</strong></>} />
          <FooterItem icon="⌖" text="Wilmington, New Castle County, Delaware, USA" />
          <FooterItem icon="◌" text={<><strong>Founder:</strong> Imran Ahmed, CPA</>} />
          <FooterItem
            icon="✉"
            text={
              <a style={styles.link} href="mailto:support@asklawly.com">
                support@asklawly.com
              </a>
            }
          />
        </div>
        <div style={styles.footerLinks}>
          <a style={styles.link} href="/terms">Terms of Use</a>
          <span style={styles.footerDivider}>|</span>
          <a style={styles.link} href="/privacy">Privacy Policy</a>
        </div>
      </section>
    </div>
  );
}

const TrustItem = ({ icon, title, text }) => (
  <article style={styles.trustItem}>
    <div style={styles.trustIcon}>{icon}</div>
    <div>
      <h2 style={styles.trustTitle}>{title}</h2>
      <p style={styles.trustText}>{text}</p>
    </div>
  </article>
);

const PolicySection = ({ id, number, title, children }) => (
  <section id={id} style={styles.policySection}>
    <h2 style={styles.sectionTitle}>{number}) {title}</h2>
    {children}
  </section>
);

const ServiceBlock = ({ title, children }) => (
  <div style={styles.serviceBlock}>
    <h3 style={styles.serviceTitle}>{title}</h3>
    <ul style={styles.list}>{children}</ul>
  </div>
);

const Callout = ({ id, icon, title, tone, children }) => {
  const isGreen = tone === "green";
  return (
    <section
      id={id}
      style={{
        ...styles.callout,
        ...(isGreen ? styles.calloutGreen : null),
      }}
    >
      <div
        style={{
          ...styles.calloutIcon,
          ...(isGreen ? styles.calloutIconGreen : null),
        }}
      >
        {icon}
      </div>
      <div>
        <h2
          style={{
            ...styles.calloutTitle,
            ...(isGreen ? styles.calloutTitleGreen : null),
          }}
        >
          {title}
        </h2>
        {children}
      </div>
    </section>
  );
};

const FooterItem = ({ icon, text }) => (
  <div style={styles.footerItem}>
    <span style={styles.footerIcon}>{icon}</span>
    <span>{text}</span>
  </div>
);

const styles = {
  page: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "18px 16px 44px",
    boxSizing: "border-box",
    background: "#F5F7FA",
    fontFamily: "sans-serif",
  },
  pageMobile: {
    padding: "14px 12px 34px",
  },

  header: {
    padding: "12px 0 14px",
  },
  title: {
    margin: 0,
    color: "#0F172A",
    fontSize: 34,
    lineHeight: 1.08,
    fontWeight: 950,
    letterSpacing: "-0.9px",
  },
  titleMobile: {
    fontSize: 30,
    textAlign: "center",
    letterSpacing: "-0.65px",
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 10,
    color: "#475569",
    fontSize: 13.5,
    lineHeight: 1.4,
    fontWeight: 750,
  },
  metaDivider: {
    color: "#CBD5E1",
  },
  intro: {
    margin: "12px 0 0",
    maxWidth: 900,
    color: "#334155",
    fontSize: 14.5,
    lineHeight: 1.6,
    fontWeight: 650,
  },

  trustGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 0,
    marginTop: 10,
    borderRadius: 18,
    border: "1px solid rgba(37,99,235,0.16)",
    background: "#FFFFFF",
    boxShadow: "0 14px 34px rgba(2,6,23,0.06)",
    overflow: "hidden",
  },
  trustGridMobile: {
    gridTemplateColumns: "1fr",
  },
  trustItem: {
    display: "flex",
    alignItems: "center",
    gap: 13,
    minHeight: 88,
    padding: 18,
    borderRight: "1px solid rgba(2,6,23,0.08)",
    boxSizing: "border-box",
  },
  trustIcon: {
    width: 48,
    height: 48,
    minWidth: 48,
    borderRadius: 16,
    background: "rgba(37,99,235,0.10)",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    fontWeight: 950,
  },
  trustTitle: {
    margin: "0 0 4px",
    color: "#0F172A",
    fontSize: 14.5,
    lineHeight: 1.25,
    fontWeight: 950,
  },
  trustText: {
    margin: 0,
    color: "#475569",
    fontSize: 13.25,
    lineHeight: 1.45,
    fontWeight: 650,
  },

  mobileIndex: {
    marginTop: 14,
    borderRadius: 16,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 10px 26px rgba(2,6,23,0.05)",
    overflow: "hidden",
  },
  mobileIndexButton: {
    appearance: "none",
    width: "100%",
    border: "none",
    background: "#FFFFFF",
    color: "#0F172A",
    padding: "13px 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    fontSize: 14.5,
    fontWeight: 950,
  },
  mobileIndexList: {
    borderTop: "1px solid rgba(2,6,23,0.08)",
    display: "grid",
    gap: 0,
  },
  mobileIndexLink: {
    padding: "10px 14px",
    color: "#2563EB",
    fontSize: 13.5,
    fontWeight: 800,
    textDecoration: "none",
    borderBottom: "1px solid rgba(2,6,23,0.06)",
  },

  layout: {
    display: "grid",
    gridTemplateColumns: "270px minmax(0, 1fr)",
    gap: 14,
    alignItems: "start",
    marginTop: 14,
  },
  layoutMobile: {
    display: "block",
  },

  sidebar: {
    position: "sticky",
    top: 16,
    alignSelf: "start",
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 12px 30px rgba(2,6,23,0.055)",
    padding: 16,
    boxSizing: "border-box",
  },
  sidebarTitle: {
    color: "#0F172A",
    fontSize: 16,
    fontWeight: 950,
    marginBottom: 12,
  },
  navList: {
    display: "grid",
    gap: 5,
  },
  navLink: {
    display: "grid",
    gridTemplateColumns: "26px minmax(0, 1fr)",
    alignItems: "center",
    gap: 9,
    padding: "9px 8px",
    borderRadius: 12,
    color: "#334155",
    fontSize: 13.25,
    lineHeight: 1.3,
    fontWeight: 800,
    textDecoration: "none",
  },
  navLinkActive: {
    background: "rgba(37,99,235,0.08)",
    color: "#2563EB",
  },
  navIcon: {
    width: 22,
    height: 22,
    borderRadius: 8,
    background: "rgba(30,42,120,0.07)",
    color: "#1E2A78",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 950,
  },
  sidebarNote: {
    display: "grid",
    gridTemplateColumns: "32px minmax(0, 1fr)",
    gap: 10,
    marginTop: 16,
    padding: 12,
    borderRadius: 14,
    border: "1px solid rgba(37,99,235,0.14)",
    background: "rgba(37,99,235,0.045)",
    color: "#334155",
    fontSize: 12.5,
    lineHeight: 1.45,
    fontWeight: 650,
  },
  sidebarNoteIcon: {
    width: 28,
    height: 28,
    borderRadius: 10,
    color: "#2563EB",
    background: "rgba(37,99,235,0.10)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 950,
  },
  sidebarNoteText: {
    margin: "4px 0 0",
  },

  policyPanel: {
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 14px 34px rgba(2,6,23,0.06)",
    padding: 20,
    boxSizing: "border-box",
  },
  policySection: {
    paddingTop: 6,
    paddingBottom: 10,
    borderBottom: "1px solid rgba(2,6,23,0.08)",
    scrollMarginTop: 16,
  },
  sectionTitle: {
    margin: "0 0 10px",
    color: "#0F172A",
    fontSize: 20,
    lineHeight: 1.25,
    fontWeight: 950,
    letterSpacing: "-0.3px",
  },
  p: {
    margin: "8px 0",
    color: "#334155",
    fontSize: 14.5,
    lineHeight: 1.65,
    fontWeight: 650,
  },
  allCaps: {
    margin: "8px 0",
    color: "#334155",
    fontSize: 13.5,
    lineHeight: 1.65,
    fontWeight: 850,
    letterSpacing: "0.1px",
  },
  list: {
    margin: "9px 0",
    paddingLeft: 20,
    color: "#334155",
    fontSize: 14.5,
    lineHeight: 1.62,
    fontWeight: 650,
  },
  li: {
    margin: "7px 0",
    paddingLeft: 2,
  },

  serviceBlock: {
    marginTop: 12,
    padding: "12px 14px",
    borderRadius: 14,
    border: "1px solid rgba(2,6,23,0.08)",
    background: "#F8FAFC",
  },
  serviceTitle: {
    margin: "0 0 6px",
    color: "#0F172A",
    fontSize: 14.5,
    fontWeight: 950,
  },

  callout: {
    display: "grid",
    gridTemplateColumns: "42px minmax(0, 1fr)",
    gap: 12,
    margin: "14px 0",
    padding: 15,
    borderRadius: 16,
    border: "1px solid rgba(37,99,235,0.18)",
    background: "rgba(37,99,235,0.045)",
    scrollMarginTop: 16,
  },
  calloutGreen: {
    border: "1px solid rgba(16,185,129,0.22)",
    background: "rgba(16,185,129,0.065)",
  },
  calloutIcon: {
    width: 36,
    height: 36,
    borderRadius: 13,
    background: "rgba(37,99,235,0.10)",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    fontWeight: 950,
  },
  calloutIconGreen: {
    background: "rgba(16,185,129,0.12)",
    color: "#059669",
  },
  calloutTitle: {
    margin: "0 0 6px",
    color: "#2563EB",
    fontSize: 15,
    lineHeight: 1.25,
    fontWeight: 950,
  },
  calloutTitleGreen: {
    color: "#047857",
  },
  calloutText: {
    margin: "7px 0 0",
    color: "#334155",
    fontSize: 13.75,
    lineHeight: 1.58,
    fontWeight: 650,
  },

  contactBox: {
    marginTop: 14,
    padding: 16,
    borderRadius: 16,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#F8FAFC",
    scrollMarginTop: 16,
  },
  contactTitle: {
    margin: "0 0 8px",
    color: "#0F172A",
    fontSize: 18,
    fontWeight: 950,
  },

  footerPanel: {
    marginTop: 14,
    padding: 18,
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 12px 30px rgba(2,6,23,0.055)",
    textAlign: "center",
  },
  footerTitle: {
    margin: "0 0 13px",
    color: "#0F172A",
    fontSize: 18,
    fontWeight: 950,
    letterSpacing: "-0.25px",
  },
  footerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 10,
    alignItems: "center",
  },
  footerGridMobile: {
    gridTemplateColumns: "1fr",
  },
  footerItem: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 7,
    minHeight: 34,
    color: "#334155",
    fontSize: 12.8,
    lineHeight: 1.35,
    fontWeight: 700,
  },
  footerIcon: {
    color: "#2563EB",
    fontSize: 15,
    fontWeight: 950,
  },
  footerLinks: {
    marginTop: 13,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
    fontSize: 13.5,
    fontWeight: 850,
  },
  footerDivider: {
    color: "#CBD5E1",
  },
  link: {
    color: "#2563EB",
    fontWeight: 850,
    textDecoration: "none",
  },
};
